import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Subprocess, SubprocessStatus, SubprocessStatusLabels } from './types';
import { phaseFormService } from './phaseFormService';
import { 
  setSubprocesses, 
  setSubprocessLoading, 
  setSubprocessError,
  removeSubprocess 
} from './phaseFormSlice';
import { RootState } from '../../store/store';
import { Pagination } from '../process/Pagination';

interface SubprocessListProps {
  phaseId: string;
}

export const SubprocessList: React.FC<SubprocessListProps> = ({ phaseId }) => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSubprocess, setSelectedSubprocess] = useState<Subprocess | null>(null);
  const { items: subprocesses, total, loading } = useSelector((state: RootState) => state.phaseForm.subprocesses);

  const fetchSubprocesses = async (pageNumber: number) => {
    try {
      dispatch(setSubprocessLoading(true));
      const response = await phaseFormService.getSubprocesses(phaseId, pageNumber);
      dispatch(setSubprocesses(response.data));
    } catch (error) {
      dispatch(setSubprocessError('Erro ao carregar subprocessos'));
    } finally {
      dispatch(setSubprocessLoading(false));
    }
  };

  useEffect(() => {
    fetchSubprocesses(page);
  }, [page, phaseId]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleDeleteClick = (subprocess: Subprocess) => {
    setSelectedSubprocess(subprocess);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSubprocess) return;

    try {
      await phaseFormService.deleteSubprocess(phaseId, selectedSubprocess.id);
      dispatch(removeSubprocess(selectedSubprocess.id));
      setDeleteDialogOpen(false);
    } catch (error) {
      dispatch(setSubprocessError('Erro ao remover subprocesso'));
    }
  };

  const getStatusColor = (status: SubprocessStatus) => {
    switch (status) {
      case SubprocessStatus.COMPLETED:
        return 'success.main';
      case SubprocessStatus.IN_PROGRESS:
        return 'info.main';
      case SubprocessStatus.CANCELLED:
        return 'error.main';
      default:
        return 'text.secondary';
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Subprocessos</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => {/* TODO: Implement create subprocess */}}
        >
          Novo Subprocesso
        </Button>
      </Box>

      {loading && <LinearProgress />}

      <List>
        {subprocesses.map((subprocess) => (
          <Card key={subprocess.id} sx={{ mb: 1 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="subtitle1">
                    {subprocess.name}
                    {subprocess.isTemplate && (
                      <Tooltip title="Template">
                        <Box component="span" sx={{ ml: 1, color: 'primary.main' }}>
                          (Template)
                        </Box>
                      </Tooltip>
                    )}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: getStatusColor(subprocess.status) }}
                  >
                    {SubprocessStatusLabels[subprocess.status]}
                  </Typography>
                  {subprocess.sourceValue && (
                    <Typography variant="body2" color="text.secondary">
                      Valor de origem: {subprocess.sourceValue}
                    </Typography>
                  )}
                </Box>
                {!subprocess.isTemplate && (
                  <IconButton
                    edge="end"
                    color="error"
                    onClick={() => handleDeleteClick(subprocess)}
                    disabled={subprocess.status === SubprocessStatus.IN_PROGRESS}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
              <LinearProgress
                variant="determinate"
                value={subprocess.progress}
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        ))}
      </List>

      <Pagination
        page={page}
        totalPages={Math.ceil(total / 10)}
        onPageChange={handlePageChange}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          Tem certeza que deseja excluir este subprocesso?
          {selectedSubprocess?.sourceValue && (
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Valor de origem: {selectedSubprocess.sourceValue}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 